import { FormProvider, useForm } from 'react-hook-form';
import FormSelect from '../../shared/FormSelect';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Box,
    Button,
    FormHelperText,
    InputLabel,
    Stack,
    Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import UserRole from '../../../shared/AuthRoles';
import { ArrowBack } from '@mui/icons-material';
import {
    useGetOwnerPreferencesQuery,
    useUpdateOwnerPreferencesMutation,
} from '../../../app/Slices/SevenDayApiSlice';
import toast from '../../shared/Toast';
import { useNavigate } from 'react-router';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';

const Schema = z.object({
    requestReminderInterval: z.number().min(0).max(7),
});

const OwnerNotifications = () => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();

    type FormValues = z.infer<typeof Schema>;

    const methods = useForm<FormValues>({
        defaultValues: {
            requestReminderInterval: 1,
        },
        resolver: zodResolver(Schema),
    });

    const { data: preferences } = useGetOwnerPreferencesQuery(
        loggedInUser?.company,
        { skip: !loggedInUser },
    );

    const [updatePreferences] = useUpdateOwnerPreferencesMutation();

    const { control, handleSubmit, reset } = methods;

    useEffect(() => {
        if (preferences) reset(preferences);
    }, [preferences]);

    const onSubmit = async (data: FormValues) => {
        try {
            if (!loggedInUser?.company) return;

            await updatePreferences([loggedInUser?.company, data]).unwrap();

            toast.success('Notification preferences updated successfully');
        } catch (error) {
            toast.error('Error updating notification preferences');
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={1} sx={{ pt: '30px' }}>
                    <Typography variant='subHeading'>
                        Reservation Notifications
                    </Typography>
                    <div>
                        <InputLabel>Pending Reservation Reminders</InputLabel>
                        <FormSelect
                            name='requestReminderInterval'
                            control={control}
                            options={[
                                {
                                    value: 0,
                                    label: 'Never',
                                },
                                {
                                    value: 1,
                                    label: 'Daily',
                                },
                                {
                                    value: 2,
                                    label: 'Every other day',
                                },
                                {
                                    value: 3,
                                    label: 'Every 3 days',
                                },
                                {
                                    value: 7,
                                    label: 'Weekly',
                                },
                            ]}
                        />
                        <FormHelperText>
                            You’ll only receive reminders if you have any
                            requests pending over 24 hours
                        </FormHelperText>
                    </div>
                </Stack>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: '50px',
                    }}
                >
                    <CustomButton
                        startIcon={<ArrowBack />}
                        size='small'
                        onClick={() => navigate('/adminCentre/host')}
                        color='primary'
                    >
                        <Typography sx={{ fontSize: '12px' }}>
                            Back to Admin Centre
                        </Typography>
                    </CustomButton>
                    <Button variant='contained' type='submit'>
                        Save
                    </Button>
                </Box>
            </form>
        </FormProvider>
    );
};

export default OwnerNotifications;
